import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import {
    Autocomplete,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Skeleton,
    TextField,
    Button,
    Typography,
} from "@mui/material";
import { Grid } from "@material-ui/core";
import { DataGrid, GridToolbar, GridColDef } from "@material-ui/data-grid";
import { makeGETRequestOnUserService, makeDELETERequestOnUserServiceWithQueryParameter } from "../../Api/api";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import FullScreenDialog from "../../components/Dialog/FullScreenDialog";
import InvestorProfileDetails from "../../components/MutualFunds/InvestorProfileDetails";
import { appContext } from "../../App";
import {
    canSeeAllLeads,
    hasAdminPermission,
    isVisibleLead,
} from "../../Helpers/adminPermissions";
import { IUser } from "../../components/YieldCalculator/Interfaces";

export interface UserDetails {
    firstName?: string;
    lastName?: string;
    emailId?: string;
    mobileNumber?: string;
    accountType?: string;
    smestID?: string;
    dateOfBirth?: string;
    leadsstatus?: string;
}

export interface MFInvestorProfileRecord {
    uuid: string;
    user: string;
    investor_profile_id: string;
    inserted_at: string;
    updated_at: string;
    user_details?: UserDetails;
    is_mf_investor_profile: boolean;
    lead_owner?: string;
    leadownerphone?: string;
    leadowneremail?: string;
}

interface MFEligibleUser {
    uuid: string;
    name: string;
    email: string;
    mobile: string;
    smestId: string;
}

const InvestorProfile = () => {
    const toastr = require("toastr");
    const { authUser } = useContext(appContext);
    const [investorProfiles, setInvestorProfiles] = useState<
        MFInvestorProfileRecord[]
    >([]);
    const [loading, setLoading] = useState(true);
    const [selectedUserForView, setSelectedUserForView] = useState<any>(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [addInvestorOpen, setAddInvestorOpen] = useState(false);
    const [eligibleUsers, setEligibleUsers] = useState<MFEligibleUser[]>([]);
    const [selectedEligibleUser, setSelectedEligibleUser] = useState<MFEligibleUser | null>(null);
    const [eligibleUsersLoading, setEligibleUsersLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedUserForDelete, setSelectedUserForDelete] =
        useState<MFInvestorProfileRecord | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const fetchInvestorProfile = useCallback(async () => {
        try {
            setLoading(true);
            const response = await makeGETRequestOnUserService(
                "/mutual_fund/investor_profile/list/"
            );
            if (response?.status !== 200) {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Failed to fetch investor profiles."
                );
                setInvestorProfiles([]);
                return;
            }
            const profiles = response?.data?.data;
            setInvestorProfiles(
                Array.isArray(profiles) ? profiles : []
            );
        } catch (error: any) {
            console.error(
                "Error fetching investor profiles:",
                error
            );
            toastr.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong while fetching investor profiles."
            );
            setInvestorProfiles([]);
        } finally {
            setLoading(false);
        }
    }, [toastr]);

    useEffect(() => {
        fetchInvestorProfile();
    }, [fetchInvestorProfile]);


    const handleView = (row: any) => {
        setSelectedUserForView(row);
        setViewDialogOpen(true);
    };

    const handleInvestments = (row: any) => {
        const params = new URLSearchParams({
            name: row.user_name || "MF investor",
            email: row.email || "",
            mobile: row.mobile || "",
            smestId: row.smest_id || "",
            profileId: row.investor_profile_id || "",
            profileReady: row.is_mf_investor_profile ? "true" : "false",
        });
        const url = `/mf_investor_profile/${encodeURIComponent(
            row.user_uuid
        )}/investments?${params.toString()}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const openAddInvestor = async () => {
        setAddInvestorOpen(true);
        setSelectedEligibleUser(null);
        setEligibleUsersLoading(true);
        try {
            const response = await makeGETRequestOnUserService("/user/user_list/");
            const existingUserIds = new Set(investorProfiles.map((profile) => profile.user));
            const users = Array.isArray(response?.data) ? response.data as IUser[] : [];
            setEligibleUsers(users
                .filter((user) => user.areKYCDetailsVerified && !existingUserIds.has(user.uuid))
                .filter((user) => isVisibleLead(user.leadowner))
                .map((user) => ({
                    uuid: user.uuid,
                    name: [user.firstName, user.lastName].filter(Boolean).join(" ") || "SMEST user",
                    email: user.emailId || "",
                    mobile: user.mobileNumber || "",
                    smestId: user.smestID || "",
                }))
                .sort((left, right) => left.name.localeCompare(right.name)));
        } catch (error: any) {
            toastr.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Unable to load eligible users."
            );
            setEligibleUsers([]);
        } finally {
            setEligibleUsersLoading(false);
        }
    };

    const openNewInvestorDashboard = () => {
        if (!selectedEligibleUser) return;
        const params = new URLSearchParams({
            name: selectedEligibleUser.name,
            email: selectedEligibleUser.email,
            mobile: selectedEligibleUser.mobile,
            smestId: selectedEligibleUser.smestId,
            profileReady: "false",
            startKyc: "true",
        });
        const url = `/mf_investor_profile/${encodeURIComponent(
            selectedEligibleUser.uuid
        )}/investments?${params.toString()}`;
        window.open(url, "_blank", "noopener,noreferrer");
        setAddInvestorOpen(false);
        setSelectedEligibleUser(null);
    };


    const columns: GridColDef[] = [
        {
            field: "sr_no",
            headerName: "Sr. No.",
            width: 100,
            sortable: false,
        },
        {
            field: "inserted_at",
            headerName: "Created At",
            width: 180,
        },
        {
            field: "is_mf_investor_profile",
            headerName: "Investor Profile",
            width: 190,
        },
        {
            field: "user_name",
            headerName: "Name",
            width: 190,
        },
        {
            field: "email",
            headerName: "Email",
            width: 230,
        },
        {
            field: "mobile",
            headerName: "Mobile",
            width: 130,
        },
        {
            field: "dob",
            headerName: "DOB",
            width: 110,
        },
        {
            field: "lead_owner",
            headerName: "Lead Owner",
            width: 160,
        },
        {
            field: "leads_status",
            headerName: "Leads Status",
            width: 170,
        },
        {
            field: "smest_id",
            headerName: "SMEST ID",
            width: 150,
        },
        {
            field: "investments",
            headerName: "Mutual Funds",
            width: 155,
            sortable: false,
            renderCell: (p: any) => (
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleInvestments(p.row);
                    }}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 7,
                        border: "1px solid #d9ddf6",
                        background: "#f4f5ff",
                        color: "#3730a3",
                        padding: "7px 11px",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 700,
                    }}
                >
                    <AccountBalanceWalletOutlinedIcon style={{ fontSize: 17 }} />
                    Manage MF
                </button>
            ),
        },
        {
            field: "view", headerName: "View", width: 90, sortable: false,
            renderCell: (p: any) => (
                <VisibilityIcon
                    style={{ cursor: "pointer", fontSize: 20 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleView(p.row);
                    }}
                />
            ),
        },
    ];

    const formatCreatedAt = (date?: string) => {
        if (!date) return "";

        const parsedDate = new Date(date);

        if (isNaN(parsedDate.getTime())) {
            return date;
        }

        return parsedDate.toLocaleString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });
    };

    const mapProfileToRow = (
        profile: MFInvestorProfileRecord,
        index: number
    ) => ({
        id: profile.uuid || index,
        uuid: profile.uuid,
        user_uuid: profile.user,
        investor_profile_id: profile.investor_profile_id,
        sr_no: index + 1,
        inserted_at: formatCreatedAt(profile.inserted_at),
        is_mf_investor_profile: profile.is_mf_investor_profile,
        user_name: [
            profile.user_details?.firstName,
            profile.user_details?.lastName,
        ]
            .filter(Boolean)
            .join(" "),

        email: profile.user_details?.emailId || "",
        mobile: profile.user_details?.mobileNumber || "",
        dob: profile.user_details?.dateOfBirth || "",
        lead_owner: profile.lead_owner || "",
        leads_status: profile.user_details?.leadsstatus || "",
        smest_id: profile.user_details?.smestID || "",
    });

    // An admin without `can_see_all` sees only their own leads, as on the
    // Users page. The full list is kept for "Add investor", which must still
    // skip anyone who already has a profile, whoever's lead they are.
    const leadsOnly = useMemo(() => !canSeeAllLeads(), []);
    const visibleProfiles = useMemo(
        () => investorProfiles.filter((profile) => isVisibleLead(profile.lead_owner)),
        [investorProfiles]
    );
    const rows = visibleProfiles.map(mapProfileToRow);

    const handleOpenDeleteDialog = () => {
        setSelectedUserForDelete(null);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        if (deleteLoading) {
            return;
        }
        setDeleteDialogOpen(false);
        setSelectedUserForDelete(null);
    };

    const handleDeleteClick = () => {
        if (!selectedUserForDelete) {
            toastr.error("Please select a mobile number.");
            return;
        }

        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedUserForDelete?.user) {
            toastr.error("User UUID not found.");
            return;
        }

        try {
            setDeleteLoading(true);

            const response = await makeDELETERequestOnUserServiceWithQueryParameter(
                `/mutual_fund/mf_kyc_records/delete/${selectedUserForDelete.user}/`
            );

            if (
                response?.status === 200 &&
                response?.data?.success
            ) {
                toastr.success(
                    response?.data?.message ||
                    "MF records deleted successfully."
                );

                setDeleteConfirmOpen(false);
                setDeleteDialogOpen(false);
                setSelectedUserForDelete(null);

                await fetchInvestorProfile();
            } else {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Failed to delete MF records."
                );
            }
        } catch (error: any) {
            console.error("Delete MF records error:", error);

            toastr.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Something went wrong while deleting MF records."
            );
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <>
            <Grid className="QuotesDataGrid">
                <Paper>
                    <Grid style={{ margin: "10px 0", padding: "0 10px" }}>
                        <Grid container alignItems="center" spacing={3}>
                            <Grid item xs>
                                <div style={{ fontSize: "36px", marginLeft: "30px" }}>
                                    MF Investor Profile Dashboard
                                </div>
                                {leadsOnly ? (
                                    <div style={{ fontSize: "13px", marginLeft: "30px", color: "#667085" }}>
                                        Showing your leads only
                                    </div>
                                ) : null}
                            </Grid>
                            <Grid item xs="auto">
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={handleOpenDeleteDialog}
                                        disabled={loading}
                                        sx={{
                                            color: "#d32f2f",
                                            borderColor: "#d32f2f",
                                            textTransform: "none",
                                            height: "40px",
                                            "&:hover": {
                                                borderColor: "#b71c1c",
                                                backgroundColor: "#fff5f5",
                                            },
                                        }}
                                    >
                                        <DeleteIcon />
                                    </Button>
                                    <button
                                        type="button"
                                        onClick={openAddInvestor}
                                        style={{
                                            border: "none",
                                            borderRadius: 8,
                                            background: "#262349",
                                            color: "#fff",
                                            cursor: "pointer",
                                            fontSize: 13,
                                            fontWeight: 700,
                                            padding: "10px 16px",
                                            marginRight: 30,
                                        }}
                                    >
                                        Add MF Investor
                                    </button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                    {hasAdminPermission(authUser, "MF Investor Profile") ? (
                        <Box
                            sx={{
                                height: "100vh",
                                width: "100%",
                                px: [2, 9],
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: "bold",
                                },
                                "& .MuiDataGrid-row": {
                                    cursor: "pointer",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    background: "#f5f7ff !important",
                                },
                            }}
                        >
                            {loading ? (
                                <Box>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <Skeleton
                                            key={i}
                                            height={50}
                                            animation="wave"
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <DataGrid
                                    pagination
                                    rows={rows}
                                    columns={columns}
                                    components={{
                                        Toolbar: GridToolbar,
                                    }}
                                />
                            )}
                        </Box>
                    ) : null}
                </Paper>
            </Grid>

            <FullScreenDialog
                show={viewDialogOpen}
                heading={`${selectedUserForView?.user_name || ""}`}
                handleClose={() => setViewDialogOpen(false)}
                showSaveButton={true}
                saveButtonText="Save"
                onSumitSaveButton={() => {
                    setViewDialogOpen(false);
                    fetchInvestorProfile();
                }}
                inputProps={
                    <InvestorProfileDetails
                        data={{
                            user:
                                selectedUserForView?.user_uuid || "",
                        }}
                    />
                }
            />

            <Dialog
                open={addInvestorOpen}
                onClose={() => !eligibleUsersLoading && setAddInvestorOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Add MF Investor</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Autocomplete
                            options={eligibleUsers}
                            value={selectedEligibleUser}
                            loading={eligibleUsersLoading}
                            onChange={(_, value) => setSelectedEligibleUser(value)}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            getOptionLabel={(option) => `${option.name}${option.mobile ? ` · ${option.mobile}` : ""}`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select a KYC-verified user"
                                    placeholder="Search by name or mobile"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {eligibleUsersLoading && <CircularProgress size={18} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            noOptionsText={eligibleUsersLoading
                                ? "Loading eligible users…"
                                : "No KYC-verified users are awaiting MF setup"}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <button
                        type="button"
                        onClick={() => setAddInvestorOpen(false)}
                        disabled={eligibleUsersLoading}
                        style={{ border: "1px solid #d8dbea", borderRadius: 8, background: "#fff", padding: "9px 16px" }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={openNewInvestorDashboard}
                        disabled={!selectedEligibleUser || eligibleUsersLoading}
                        style={{ border: "none", borderRadius: 8, background: "#262349", color: "#fff", padding: "9px 16px", fontWeight: 700 }}
                    >
                        Open MF dashboard
                    </button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        color: "#262349",
                    }}
                >
                    Delete Investor Profile
                </DialogTitle>

                <DialogContent>
                    <Typography
                        sx={{
                            mb: 2,
                            fontSize: "14px",
                            color: "#666",
                        }}
                    >
                        Search and select the user's mobile number whose
                        Investor Profile you want to delete.
                    </Typography>

                    <Autocomplete
                        options={visibleProfiles}
                        value={selectedUserForDelete}
                        onChange={(_, newValue) => {
                            setSelectedUserForDelete(newValue);
                        }}
                        getOptionLabel={(option) =>
                            option?.user_details?.mobileNumber || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                            option.uuid === value.uuid
                        }
                        filterOptions={(options, { inputValue }) => {
                            const searchValue =
                                inputValue.toLowerCase().trim();

                            return options.filter((option) => {
                                const mobile =
                                    option?.user_details?.mobileNumber
                                        ?.toLowerCase() || "";

                                return mobile.includes(searchValue);
                            });
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.uuid}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: "#262349",
                                        }}
                                    >
                                        {option?.user_details
                                            ?.mobileNumber || "-"}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            color: "#777",
                                        }}
                                    >
                                        {[
                                            option?.user_details
                                                ?.firstName,
                                            option?.user_details
                                                ?.lastName,
                                        ]
                                            .filter(Boolean)
                                            .join(" ") || "-"}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search Mobile Number"
                                placeholder="Enter mobile number"
                                fullWidth
                            />
                        )}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        disabled={deleteLoading}
                        sx={{
                            textTransform: "none",
                            color: "#555",
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteClick}
                        disabled={
                            !selectedUserForDelete ||
                            deleteLoading
                        }
                        sx={{
                            textTransform: "none",
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => {
                    if (!deleteLoading) {
                        setDeleteConfirmOpen(false);
                    }
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        color: "#262349",
                    }}
                >
                    Confirm Delete
                </DialogTitle>

                <DialogContent>
                    <Typography
                        sx={{
                            fontSize: "15px",
                            color: "#444",
                            lineHeight: 1.6,
                        }}
                    >
                        Do you want to confirm delete this user's MF
                        Investor Profile?
                    </Typography>

                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            backgroundColor: "#f7f7f9",
                            borderRadius: "6px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "12px",
                                color: "#777",
                            }}
                        >
                            Mobile Number
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: "15px",
                                fontWeight: 600,
                                color: "#262349",
                            }}
                        >
                            {selectedUserForDelete
                                ?.user_details?.mobileNumber || "-"}
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteConfirmOpen(false)}
                        disabled={deleteLoading}
                        sx={{
                            textTransform: "none",
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={deleteLoading}
                        startIcon={
                            deleteLoading ? undefined : <DeleteIcon />
                        }
                        sx={{
                            textTransform: "none",
                        }}
                    >
                        {deleteLoading
                            ? "Deleting..."
                            : "Confirm Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
};

export default InvestorProfile;
